import { bitrateFor } from "./codecs.ts";
import { isVisual, type Project } from "./timeline.ts";

/**
 * What the export dialog lets you choose, kept apart from export.ts so the
 * dialog can show sizes and estimates without loading the exporter, which
 * stays out of the initial bundle.
 */
export type Quality = "high" | "standard" | "small";

export interface ExportSettings {
  /** Output height; null keeps the composition's own size. */
  height: number | null;
  fps: number;
  quality: Quality;
}

/** Relative to the rate codecs.ts picks, which is the "standard" one. */
const QUALITY_FACTOR: Record<Quality, number> = { high: 1.6, standard: 1, small: 0.55 };

const LADDER = [2160, 1440, 1080, 720, 480];

const even = (n: number): number => Math.max(2, Math.round(n / 2) * 2);

export function defaultSettings(project: Project): ExportSettings {
  return { height: null, fps: project.fps, quality: "standard" };
}

/** The composition's own size, then every standard height below it. */
export function resolutionChoices(project: Project): { height: number | null; label: string }[] {
  const choices: { height: number | null; label: string }[] = [
    { height: null, label: `${project.width}×${project.height}` },
  ];
  for (const height of LADDER) {
    if (height >= project.height) continue;
    const { width } = outputSize(project, { height, fps: project.fps, quality: "standard" });
    choices.push({ height, label: `${height}p · ${width}×${height}` });
  }
  return choices;
}

/** The composition's rate and the common ones below it. Above it would only duplicate frames. */
export function frameRateChoices(project: Project): number[] {
  return [...new Set([project.fps, 60, 30, 24])].filter((fps) => fps <= project.fps).sort((a, b) => b - a);
}

export function outputSize(project: Project, settings: ExportSettings): { width: number; height: number } {
  const scale = settings.height ? settings.height / project.height : 1;
  return { width: even(project.width * scale), height: even(project.height * scale) };
}

export function videoBitrate(project: Project, settings: ExportSettings): number {
  const { width, height } = outputSize(project, settings);
  return Math.round(bitrateFor(width, height) * (settings.fps / 30) * QUALITY_FACTOR[settings.quality]);
}

const AUDIO_BITRATE = 128_000;

/** A timeline with nothing to see — no video, no picture, no title — exports as sound alone. */
export const isAudioOnly = (project: Project): boolean =>
  project.clips.length > 0 && !project.clips.some((clip) => isVisual(clip.kind));

/** An audio-only export is AAC at a fixed rate, so this one is close to exact. */
export function estimatedAudioBytes(duration: number): number {
  return (AUDIO_BITRATE * duration) / 8;
}

/**
 * A ceiling, and shown as one. The encoder runs at a variable bitrate, so the
 * rate it is given is the most it will average; on footage that compresses
 * well (still scenes, little motion) it spends a fraction of it. A 13-second
 * export estimated at 4.4 MB came out at 1 MB. Constant bitrate would make the
 * number come true only by padding the file with bits that buy nothing.
 */
export function estimatedBytes(
  project: Project,
  settings: ExportSettings,
  duration: number,
  hasAudio: boolean,
): number {
  return ((videoBitrate(project, settings) + (hasAudio ? AUDIO_BITRATE : 0)) * duration) / 8;
}
